import { NoteClass, WordUnit } from "../units/wordUnit";

/** convert digits into words */
export function handleNumbers(unit: WordUnit): WordUnit {
  unit = romanNumeralsToNumbers(unit);

  const result = substituteNumbers(unit.input);
  if (typeof result === 'string') {
    return { ...unit, input: result };
  }
  const input = result.join('-');
  const notes = unit.notes ? unit.notes.concat(NoteClass.Hyphenated) : [NoteClass.Hyphenated];
  return { ...unit, input, notes };
}

/** if 'word' is a number, convert it to words, otherwise simply return the original word */
export function substituteNumbers(word: string): string | string[] {
  if (word.match(/\d/) === null) {
    return word;  // skip checks if there aren't any digits
  }
  const words = numberToEnglish(word);
  return words !== null ? words : word;
}

/** if possible, convert digits with optional symbols to English words */
export function numberToEnglish(str: string): string[] | null {
  if (str.indexOf('-') !== -1) {
    const result = handleDash(str);
    if (result !== null) {
      return result;
    }
  }

  const withEnding = tryEndings(str);
  if (withEnding !== null) {
    return withEnding;
  }

  if (str.endsWith('1st') || str.endsWith('2nd') || str.endsWith('3rd') || str.endsWith('th')) {
    return nthToEnglish(str);
  } else if (str.startsWith('$')) {
    const numbers = realToEnglish(str.substring(1));
    return numbers === null ? null : addMaybePlural(numbers, 'dollar');
  } else if (str.startsWith('€')) {
    const numbers = realToEnglish(str.substring(1));
    return numbers === null ? null : addMaybePlural(numbers, 'euro');
  } else if (str.startsWith('£')) {
    const numbers = realToEnglish(str.substring(1));
    return numbers === null ? null : addMaybePlural(numbers, 'pound');
  } else if (str.startsWith('#')) {
    const numbers = realToEnglish(str.substring(1));
    return numbers === null ? null : ['number'].concat(numbers);
  } else if (str.startsWith('~')) {
    const numbers = realToEnglish(str.substring(1));
    return numbers === null ? null : ['about'].concat(numbers);
  } else if (str.startsWith('−')) {
    const numbers = realToEnglish(str.substring(1));
    return numbers === null ? null : ['minus'].concat(numbers);
  } else if (str.startsWith('√')) {
    const numbers = realToEnglish(str.substring(1));
    return numbers === null ? null : ['square-root-of'].concat(numbers);
  } else if (str.startsWith('-√') || str.startsWith('−√')) {
    const numbers = realToEnglish(str.substring(2));
    return numbers === null ? null : ['minus-square-root-of'].concat(numbers);
  } else if (str.endsWith('s')) {
    const sub = str.substring(0, str.length - (str.endsWith("'s") ? 2 : 1));
    const numbers = realToEnglish(sub);
    if (numbers !== null && numbers.length > 0) {
      const lastWord = numbers[numbers.length - 1];
      if (lastWord.endsWith('y')) { // e.g. <sixty> => <sixties>
        numbers[numbers.length - 1] = lastWord.substring(0, lastWord.length - 1) + 'ies';
      } else if (lastWord.endsWith('x')) {  // e.g. <six> + <es>
        numbers[numbers.length - 1] = lastWord + 'es';
      } else {  // e.g. <three> + <s>
        numbers[numbers.length - 1] = lastWord + 's';
      }
    }
    return numbers === null ? null : numbers;
  } else {
    const fraction = handleFraction(str);
    if (fraction !== null) {
      return fraction;
    }

    const time = handleTime(str);
    if (time !== null) {
      return time;
    }
  }
  return realToEnglish(str);
}

/**
 * symbols that can appear at the end of numbers,
 * with the the string that should be used & whether it should be plural.
 * making it plural should actually depend on its part of speech,
 * e.g., "90 degree day" versus "it's 90 degrees"
 * or "100 meter race" versus "100 meters long"
 */
const endings: Record<string, [string, string]> = {
  '%': ["percent", ""],
  '°': ["degree", ""],
  '+': ["plus", ""],
  '$': ["dollar", "s"],
  '€': ["euro", "s"],
  // measurements
  'mph': ["miles-per-hour", ""],
  'kph': ["kilometers-per-hour", ""],
  'km': ["kilometer", "s"],
  'cm': ["centimeter", "s"],
  'mm': ["millimeter", "s"],
  'kg': ["kilogram", "s"],
  'mg': ["milligram", "s"],
  'ml': ["milliliter", "s"],
  'mi': ["mile", "s"],
  'in': ["inch", "es"],
  'ft': ["foot", ""],
  'lb': ["pound", "s"],
  'kb': ["kilobyte", "s"],
  'mb': ["megabyte", "s"],
  'gb': ["gigabyte", "s"],
  'kcal': ["kilo-calorie", "s"],
  'k': ["k", ""], // or thousand
  'm': ["meter", "s"],  // or million
  'g': ["gram", "s"],
  'l': ["liter", "s"],
  // time
  'am': ["a.m.", ""],
  'pm': ["p.m.", ""],
  // numbers
  'mn': ["million", "s"],
  'bn': ["billion", "s"],
  // other
  'sq': ["square", ""],
  'x': ["ex", ""],
}
const endingStrs = Object.getOwnPropertyNames(endings);
/** if it's a number with one of the above endings, turn it into strings, else null */
function tryEndings(str: string): string[] | null {
  for (const e of endingStrs) {
    if (str.endsWith(e)) {
      const numbers = realToEnglish(str.substring(0, str.length - e.length));
      if (numbers !== null) {
        const entry = endings[e];
        if (numbers.length === 1 && numbers[0] === 'one') {
          return numbers.concat(entry[0]);
        } else {  // plural
          return numbers.concat(entry[0] + entry[1]);
        }
      }
    }
  }
  return null;
}


/** turn a positive number with optional decimal digits into English */
function realToEnglish(str: string): string[] | null {
  const dot = str.indexOf('.');
  if (dot === -1) {
    return integerToEnglish(str);
  }

  let strings = integerToEnglish(str.substring(0, dot));
  if (!strings) {
    return null;
  }

  const rest = str.substring(dot + 1);
  if (rest.length > 0) {
    strings = strings.concat('point');
    for (let i = 0; i < rest.length; i++) {
      const digit = integerToEnglish(rest[i]);
      if (!digit) {
        return null;  // isn't a real number
      }
      strings = strings.concat(digit);
    }
  }
  return strings;
}

/** turn a positive whole number into English */
function integerToEnglish(str: string): string[] | null {
  const array = stringToArray(str);
  if (array === null) {
    return null;
  }

  if (str.match(/^0+$/) !== null) { // string of 0's
    return ['zero'];
  } else if (array.length > 36) {
    return ['a', 'zillion'];
  }

  const pieces = asTwoPieces(array);
  if (pieces !== null) {
    return pieces;
  }

  const first = get3DigitNumber(array.slice(0, 3));
  let words = first === null ? [] : first;
  for (let i = 0; i < 11; i++) {
    const magnitude = get3DigitNumber(array.slice((i + 1) * 3, (i + 2) * 3));
    if (magnitude !== null) {
      words = [...magnitude, magnitudes[i], ...words];
    }
  }
  return words;
}

/** add a word, making it plural or singular as needed */
function addMaybePlural(numbers: string[], word: string): string[] {
  if (numbers.length === 1 && numbers[0] === 'one') {
    return numbers.concat(word);
  }
  return numbers.concat(word + 's');
}

/** try converting strings like 1st, 4th, or 1236th */
function nthToEnglish(str: string): string[] | null {
  const strs = realToEnglish(str.substring(0, str.length - 2));
  if (strs === null) {
    return null;
  }

  // try 1-19 forms, then try 20,30..90 forms
  let nth = '';
  const whichN = counting.indexOf(strs[strs.length - 1]);
  if (whichN !== -1) {
    nth = countingNth[whichN];
  } else {
    const whichTen = tens.indexOf(strs[strs.length - 1]);
    nth = whichTen === -1 ? `${strs[strs.length - 1]}th` : tensNth[whichTen];
  }

  if (strs.length === 1) {
    return [nth];
  }
  return [...strs.slice(0, strs.length - 1), nth];
}

/** handle a dash as either a negative number or range of numbers */
function handleDash(str: string): string[] | null {
  const where = str.indexOf('-');
  const last = str.lastIndexOf('-');
  if (where === 0) {
    // at start, see if it's a negative number
    const rest = numberToEnglish(str.substring(1));
    if (rest) {
      return ['minus'].concat(rest);
    }
  } else if (where > 0 && where === last) {
    // in there's a single dash in the middle, see if it's a range of numbers
    const first = numberToEnglish(str.substring(0, where));
    const rest = numberToEnglish(str.substring(where + 1));
    if (first && rest) {
      return first.concat('to').concat(rest);
    }
  }
  return null;
}

/** handle simple fractions #/# */
function handleFraction(str: string): string[] | null {
  const fraction = str.match(/(\d+)\/(\d+)(.*)/);
  if (fraction && fraction[3] === '') {
    const top = integerToEnglish(fraction[1]);
    let bottom = nthToEnglish(fraction[2] + "th");
    if (top && bottom && fraction[2] !== "1" && fraction[2] !== "0") {
      if (fraction[2] === "2") {
        bottom = fraction[1] === "1" ? ["half"] : ["halves"];
      } else if (fraction[1] !== "1") {
        bottom[bottom.length - 1] = bottom[bottom.length - 1] + "s";
      }
      return top.concat(bottom);
    }
  }
  return null;
}

/** ##:## handle clock time or hours & minutes */
function handleTime(str: string): string[] | null {
  // e.g., 1:00am or 12:34PM or 1:23 or 1:23:45
  const clock = str.match(/(\d?\d):(\d\d)(:\d\d)?(?:([AaPp])[Mm])?/);
  if (clock) {
    const hours = integerToEnglish(clock[1]);
    const minutes = integerToEnglish(clock[2]);
    if (hours && minutes) {
      let result = hours;
      if (clock[2][0] === '0' && clock[2][1] !== '0') {
        result.push('o');
      }
      if (minutes.length > 1 || minutes[0] !== 'zero') {
        result = result.concat(minutes);
      }

      if (clock[3]) {
        const seconds = integerToEnglish(clock[3].substring(1));
        if (seconds && (seconds.length > 1 || seconds[0] !== 'zero')) {
          result = result.concat('and').concat(seconds).concat('seconds');
        }
      }
      if (clock[4]) {
        result = result.concat(clock[4] === 'A' || clock[4] === 'a' ? ['a.m.'] : ['p.m.']);
      }
      return result;
    }
  }

  return null;
}

/** string as a reversed array of numbers or null if invalid, e.g., '123' -> [3, 2, 1] */
function stringToArray(s: string): number[] | null {
  const array: number[] = [];
  for (let i = 0; i < s.length; i++) {
    const d = s.charCodeAt(i);
    if (d === 0x2c) {
      // ignore commas
      continue;
    }
    if (d < 0x30 || 0x39 < d) {
      // not a digit, bail
      return null;
    }
    array.push(d - 0x30);
  }
  return array.length === 0 ? null : array.reverse();
}

/** if within 1001-1999, return something like "nineteen thirty two" */
function asTwoPieces(digits: number[]): string[] | null {
  if (digits.length !== 4 || digits[3] !== 1) { // first digit must be 1
    return null;
  }
  if (digits[0] === 0 && digits[1] === 0 && digits[2] === 0) {
    return [counting[digits[3]], "thousand"]; // e.g., "one thousand"
  }
  const a = getEnding2Digits(digits[0], digits[1]);   // last two digits
  const b = get3DigitNumber([digits[2], digits[3]]);  // first two digits
  return a === null || b === null ? null : b.concat(a);
}
function getEnding2Digits(digit0: number, digit1: number): string[] | null {
  if (digit0 === 0 && digit1 === 0) return ["hundred"];
  if (digit1 === 0) return ["o", counting[digit0]];  // e.g. "o five"
  return get3DigitNumber([digit0, digit1]); // e.g. "thirty five"
}

/**
 * get a word for a number from 0-999 that's suitable for adding on to 'thousand', 'million', etc.
 * or null if nothing should be added
 */
function get3DigitNumber(digits: number[]): string[] | null {
  const n1 = digits[0] === undefined ? 0 : digits[0];
  const n2 = digits[1] === undefined ? 0 : digits[1];
  const n3 = digits[2] === undefined ? 0 : digits[2];
  if (n1 === 0 && n2 === 0 && n3 === 0) {
    return null;
  }

  const d21 = n2 < 2 ? [counting[n2 * 10 + n1]]
    : n1 === 0 ? [tens[n2]]
      : [tens[n2], counting[n1]];
  if (n3 === 0) {
    return d21;
  }

  const d3 = [counting[n3], 'hundred'];
  const d321 = n1 === 0 && n2 === 0 ? d3
    : [...d3, 'and', ...d21];
  return d321;
}

const counting = [
  '', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen',
]

const countingNth = [
  'zeroth', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth',
  'tenth', 'eleventh', 'twelfth', 'thirteenth', 'fourteenth', 'fifteenth', 'sixteenth', 'seventeenth', 'eighteenth', 'nineteenth',
]

const tens = [
  '', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'
]

const tensNth = [
  '', '', 'twentieth', 'thirtieth', 'fortieth', 'fiftieth', 'sixtieth', 'seventieth', 'eightieth', 'ninetieth'
]

const magnitudes = [
  'thousand', 'million', 'billion', 'trillion', 'quadrillion', 'quintillion',
  'sextillion', 'septillion', 'octillion', 'nonillion', 'decillion'
]

/** if it's a Roman numeral, convert to a number (as a string) */
function romanNumeralsToNumbers(unit: WordUnit): WordUnit {
  if (unit.notes && unit.notes.includes(NoteClass.AllCaps)) {
    const s = romanNumerals[unit.input];
    if (s !== undefined) {
      // remove 'AllCaps' and return number
      const notes = unit.notes.filter(n => n !== NoteClass.AllCaps);
      if (notes.length === 0) {
        unit = { ...unit, input: s, notes: undefined };
      } else {
        unit = { ...unit, input: s, notes };
      }
    }
  }
  return unit;
}

const romanNumerals: Record<string, string> = {
  // 'i': '1',
  'ii': '2',
  'iii': '3',
  'iv': '4',
  // 'v': '5',
  'vi': '6',
  'vii': '7',
  'viii': '8',
  'ix': '9',

  // 'x': '10',
  'xi': '11',
  'xii': '12',
  'xiii': '13',
  'xiv': '14',
  'xv': '15',
  'xvi': '16',
  'xvii': '17',
  'xviii': '18',
  'xix': '19',

  // 'xx': '20',
  'xxi': '21',
  'xxii': '22',
  'xxiii': '23',
  'xxiv': '24',
  'xxv': '25',
  'xxvi': '26',
  'xxvii': '27',
  'xxviii': '28',
  'xxix': '29',

  // 'xxx': '30',
  'xxxi': '31',
  'xxxii': '32',
  'xxxiii': '33',
  'xxxiv': '34',
  'xxxv': '35',
  'xxxvi': '36',
  'xxxvii': '37',
  'xxxviii': '38',
  'xxxix': '39',

  'xl': '40',
  'xli': '41',
  'xlii': '42',
  'xliii': '43',
  'xliv': '44',
  'xlv': '45',
  'xlvi': '46',
  'xlvii': '47',
  'xlviii': '48',
  'xlix': '49',

  // 'l': '50',
  'li': '51',
  'lii': '52',
  'liii': '53',
  'liv': '54',
  'lv': '55',
  'lvi': '56',
  'lvii': '57',
  'lviii': '58',
  'lix': '59',

  'lx': '60',
  'lxi': '61',
  'lxii': '62',
  'lxiii': '63',
  'lxiv': '64',
  'lxv': '65',
  'lxvi': '66',
  'lxvii': '67',
  'lxviii': '68',
  'lxix': '69',

  'lxx': '70',
  'lxxi': '71',
  'lxxii': '72',
  'lxxiii': '73',
  'lxxiv': '74',
  'lxxv': '75',
  'lxxvi': '76',
  'lxxvii': '77',
  'lxxviii': '78',
  'lxxix': '79',

  'lxxx': '80',
  'lxxxi': '81',
  'lxxxii': '82',
  'lxxxiii': '83',
  'lxxxiv': '84',
  'lxxxv': '85',
  'lxxxvi': '86',
  'lxxxvii': '87',
  'lxxxviii': '88',
  'lxxxix': '89',

  'xc': '90',
  'xci': '91',
  'xcii': '92',
  'xciii': '93',
  'xciv': '94',
  'xcv': '95',
  'xcvi': '96',
  'xcvii': '97',
  'xcviii': '98',
  'xcix': '99',

  // 'c': '100',
}
